import { Input, InputProps, useStyleConfig } from '@chakra-ui/react';
import { formatInTimeZone } from 'date-fns-tz';
import { FocusEvent, useEffect, useMemo, useReducer } from 'react';
import { useFormContext } from 'react-hook-form';

import { useFieldValidationStates } from '../hooks/useFieldValidationStates';
import { SimpleField, SimpleXProps } from './SimpleField';
import { useSimpleFormContext } from './SimpleForm';

type SimpleInputProps = SimpleXProps &
  InputProps & {
    selectOnFocus?: boolean;
    isHighlighted?: boolean;
    inputAttrs?: InputProps;
  };

export const SimpleNativeDatePicker = ({
  name,
  label,
  placeholder,
  helper,
  size = 'md',
  ignoreOnBlur,
  selectOnFocus = false,
  isHighlighted = false,
  inputAttrs = {}, // Anything you want to pass to the input itself, for mainly text styling
  type = 'date',
  ...rest
}: SimpleInputProps) => {
  const { isInvalid } = useFieldValidationStates(name as string);
  const { simpleOnBlur } = useSimpleFormContext();
  const { register, watch } = useFormContext();

  const formProps = register(name, {
    setValueAs: (value) => {
      if (value === '') return null;

      return value;
    },
  });
  const value = watch(name);

  const onBlur = (event: any) => {
    if (ignoreOnBlur) return;

    const incomingEvent = {
      ...event,
      target: { ...event.target, name, value },
    };

    formProps.onBlur(incomingEvent);
    simpleOnBlur?.(incomingEvent);
  };

  const onFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.target.showPicker();
    if (selectOnFocus) event.target?.focus();
  };

  const borderColor = isInvalid ? 'error' : isHighlighted ? 'orange.500' : 'transparent';
  const highlightWarning = isHighlighted ? 'This triggered a threshold! ' : '';
  helper = `${highlightWarning}${helper || ''}`;

  const defaultHover = useStyleConfig('Input', { variant: 'filled' });

  // The prices we must pay.
  // actual explanation:
  const [, rerender] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    setTimeout(rerender, 1);
  }, []);

  const formattedValue = useMemo(() => {
    if (value instanceof Date || (typeof value === 'string' && value.includes('T'))) {
      if (type === 'time') {
        return formatInTimeZone(value, 'Etc/UTC', 'HH:mm');
      }

      return formatInTimeZone(value, 'Etc/UTC', 'yyyy-MM-dd');
    }

    return value;
  }, [type, value]);

  return (
    <SimpleField<InputProps> name={name} label={label} helper={helper} {...rest}>
      <Input
        type={type}
        variant="filled"
        placeholder={placeholder}
        size={size}
        value={formattedValue}
        defaultValue={formattedValue}
        {...formProps}
        onBlur={onBlur}
        onFocus={onFocus}
        px={2}
        borderColor={borderColor}
        isDisabled={rest.isDisabled}
        {...inputAttrs}
        autoComplete="off"
        _groupHover={defaultHover?.['field']?._hover}
      />
    </SimpleField>
  );
};
